import {
  Box,
  Columns,
  Container,
  Heading,
  Stack,
} from '@spaceship-fspl/components';
import {
  borderRadius,
  color,
  getSpace,
  height,
  marginBottom,
  match,
  maxWidth,
  rgba,
  text,
} from '@spaceship-fspl/styles';
import { RichText } from 'data/prismic';
import { About_PageBodyHeroPrimary } from 'data/prismic/types';
import * as React from 'react';
import styled from 'styled-components';

import HeroImageLarge from './img-about-hero-large.jpg';
import HeroImageSmall from './img-about-hero-small.jpg';

export interface AboutHeroPanelProps {
  title: string;
  welcome: Array<About_PageBodyHeroPrimary['welcome']>;
}

const HeroContainer = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
`;

const HeroImage = styled.div`
  ${borderRadius({ xs: 'none', md: 'sm' })}
  ${height({ xs: 680, md: 466, lg: 640 })}
  ${maxWidth({ xs: '100vw', md: 1001, lg: 1375 })}
  background-image: url(${HeroImageSmall});
  background-position: left;
  background-size: cover;
  margin: 0 auto;
  overflow: hidden;
  position: relative;

  ${match('md')`
    background-image: url(${HeroImageLarge});
    background-position: center;
  `}

  ::after {
    background-color: ${rgba('indigo.070', 0.65)};
    content: '';
    display: block;
    height: 100%;
    position: absolute;
    width: 100%;
  }
`;

const RichTextWrapper = styled.div`
  article {
    margin: 0;
    ${match('lg')`
      column-count: 2;
      column-gap: ${getSpace('xxl')}
    `};
  }
  article > p {
    ${marginBottom('lg')}
    ${text({ variant: 1 })}
    ${color('neutral.100')}
  }
`;

export const AboutHeroPanel: React.FC<
  React.PropsWithChildren<AboutHeroPanelProps>
> = ({ title, welcome }) => (
  <Box as="section">
    <Stack spaceY={{ xs: 'xl', md: 'xxl', lg: 'xxxl' }}>
      <Box position="relative" marginX={{ xs: 'none', md: 'lg' }}>
        <HeroImage />
        <HeroContainer>
          <Container>
            <Columns alignY="center" alignX="center">
              <Columns.Column width={{ xs: 1, lg: 10 / 12 }}>
                <Heading
                  component="h1"
                  variant={1}
                  color="neutral.000"
                  align={{ xs: 'left', md: 'center' }}
                >
                  {title}
                </Heading>
              </Columns.Column>
            </Columns>
          </Container>
        </HeroContainer>
      </Box>

      <Container>
        <Columns alignY="center" alignX="center">
          <Columns.Column width={{ xs: 1, md: 6 / 8, lg: 10 / 12 }}>
            <RichTextWrapper>
              <article>
                <RichText render={welcome} />
              </article>
            </RichTextWrapper>
          </Columns.Column>
        </Columns>
      </Container>
    </Stack>
  </Box>
);
